import {
  CollectiveAdvice,
  CoverageNeed,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";

import { AdviceProductDetailsFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { AkaAdvicePriceFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/interfaces";
import { isAllowed } from "Store/akaAdvice/interfaces/getters";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";
import { AdvicePriceCalculationRequest } from "../../interfaces/AkaAdviceState";
import { mapToAkaCriticalIllnessPriceReq } from "./mapToAkaCriticalIllnessPriceReq";
import { mapToAkaGroupLifePriceReq } from "./mapToAkaGroupLifePriceReq";
import { mapToCriticalIllnessPriceReq } from "./mapToCriticalIllnessPriceReq";
import { mapToDeathPriceReq } from "./mapToDeathPriceReq";
import { mapToDisabilityPriceReq } from "./mapToDisabilityPriceReq";

export const mapToPriceRequest = (
  isCollectiveAdvice: boolean,
  isAdvisor: boolean,
  collectiveAdvice: CollectiveAdvice,
  formValues: AkaAdvicePriceFormValues,
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
  dateOfBirth: string,
  specificProduct?: ProductName,
): AdvicePriceCalculationRequest[] => {
  if (!isDefined(specificProduct)) {
    return mapInitialPriceRequests(
      isCollectiveAdvice,
      isAdvisor,
      collectiveAdvice,
      formValues,
      adviceDetailsFormValues,
      dateOfBirth,
    );
  }

  return mapSpecificPriceRequest(
    isCollectiveAdvice,
    isAdvisor,
    collectiveAdvice,
    formValues,
    adviceDetailsFormValues,
    dateOfBirth,
    specificProduct,
  );
};

const mapInitialPriceRequests = (
  isCollectiveAdvice: boolean,
  isAdvisor: boolean,
  collectiveAdvice: CollectiveAdvice,
  formValues: AkaAdvicePriceFormValues,
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
  dateOfBirth: string,
): AdvicePriceCalculationRequest[] => {
  const initialPriceRequests: AdvicePriceCalculationRequest[] = [];

  if (isCollectiveAdvice && shouldGetPriceForAkaGroupLife(collectiveAdvice)) {
    initialPriceRequests.push(
      mapToAkaGroupLifePriceReq(
        formValues,
        dateOfBirth,
        adviceDetailsFormValues,
      ),
    );
  }
  if (shouldGetPriceForDeath(collectiveAdvice.needs.LIFE, formValues)) {
    initialPriceRequests.push(mapToDeathPriceReq(formValues));
  }
  if (
    shouldGetPriceForDisability(collectiveAdvice.needs.DISABILITY, formValues)
  ) {
    initialPriceRequests.push(
      mapToDisabilityPriceReq(isAdvisor, formValues, adviceDetailsFormValues),
    );
  }
  if (
    isCollectiveAdvice &&
    shouldGetPriceForAkaCriticalIllness(collectiveAdvice.needs.CRITICAL_ILLNESS)
  ) {
    initialPriceRequests.push(
      mapToAkaCriticalIllnessPriceReq(
        formValues,
        dateOfBirth,
        adviceDetailsFormValues,
      ),
    );
  }
  if (
    shouldGetPriceForCriticalIllness(
      collectiveAdvice.needs.CRITICAL_ILLNESS,
      formValues,
    )
  ) {
    initialPriceRequests.push(mapToCriticalIllnessPriceReq(formValues));
  }
  return initialPriceRequests;
};

const mapSpecificPriceRequest = (
  isCollectiveAdvice: boolean,
  isAdvisor: boolean,
  collectiveAdvice: CollectiveAdvice,
  formValues: AkaAdvicePriceFormValues,
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
  dateOfBirth: string,
  specificProduct?: ProductName,
): AdvicePriceCalculationRequest[] => {
  const priceRequests: AdvicePriceCalculationRequest[] = [];

  if (
    isCollectiveAdvice &&
    specificProduct === INSURANCE_NAME_VALUE.AKA_GROUP_LIFE &&
    shouldGetPriceForAkaGroupLife(collectiveAdvice)
  ) {
    priceRequests.push(
      mapToAkaGroupLifePriceReq(
        formValues,
        dateOfBirth,
        adviceDetailsFormValues,
      ),
    );
  }
  if (
    specificProduct === INSURANCE_NAME_VALUE.DEATH &&
    shouldGetPriceForDeath(collectiveAdvice.needs.LIFE, formValues)
  ) {
    priceRequests.push(mapToDeathPriceReq(formValues));
  }
  if (
    specificProduct === INSURANCE_NAME_VALUE.DISABILITY_FLEX &&
    shouldGetPriceForDisability(collectiveAdvice.needs.DISABILITY, formValues)
  ) {
    priceRequests.push(
      mapToDisabilityPriceReq(isAdvisor, formValues, adviceDetailsFormValues),
    );
  }
  if (
    isCollectiveAdvice &&
    specificProduct === INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS &&
    shouldGetPriceForAkaCriticalIllness(collectiveAdvice.needs.CRITICAL_ILLNESS)
  ) {
    priceRequests.push(
      mapToAkaCriticalIllnessPriceReq(
        formValues,
        dateOfBirth,
        adviceDetailsFormValues,
      ),
    );
  }
  if (
    specificProduct === INSURANCE_NAME_VALUE.CRITICAL_ILLNESS &&
    shouldGetPriceForCriticalIllness(
      collectiveAdvice.needs.CRITICAL_ILLNESS,
      formValues,
    )
  ) {
    priceRequests.push(mapToCriticalIllnessPriceReq(formValues));
  }

  return priceRequests;
};

const shouldGetPriceForAkaGroupLife = (
  collectiveAdvice: CollectiveAdvice,
): boolean => {
  const akaGroupLifeDeathCheckbox = isAllowed(
    collectiveAdvice.needs.LIFE,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
  );
  const incapacityOfWorkCheckbox = isAllowed(
    collectiveAdvice.needs.DISABILITY,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
  );
  const timeLimitedDisabilityInterestCheckbox = isAllowed(
    collectiveAdvice.needs.DISABILITY,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
  );
  return (
    akaGroupLifeDeathCheckbox ||
    incapacityOfWorkCheckbox ||
    timeLimitedDisabilityInterestCheckbox
  );
};

const shouldGetPriceForDeath = (
  need: CoverageNeed,
  formValues: AkaAdvicePriceFormValues,
): boolean => {
  return (
    isAllowed(need, ProductCoverageName.DEATH) &&
    (formValues.deathInsuredSumSlider ?? 0) > 0
  );
};

const shouldGetPriceForDisability = (
  need: CoverageNeed,
  formValues: AkaAdvicePriceFormValues,
): boolean => {
  return (
    isAllowed(need, ProductCoverageName.DISABILITY_FLEX_PENSION) &&
    (formValues.disabilityInsuredSumSlider ?? 0) > 0
  );
};

const shouldGetPriceForAkaCriticalIllness = (need: CoverageNeed): boolean => {
  return isAllowed(need, ProductCoverageName.AKA_CRITICAL_ILLNESS);
};

const shouldGetPriceForCriticalIllness = (
  need: CoverageNeed,
  formValues: AkaAdvicePriceFormValues,
): boolean => {
  return (
    isAllowed(need, ProductCoverageName.CRITICAL_ILLNESS) &&
    (formValues.criticalIllnessInsuredSumSlider ?? 0) > 0
  );
};
